import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CustomFieldDefinition } from '@om/shared-types';
import { AlertCircle, Loader2, Save } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { celebrationService } from '@/lib/api/celebration.service';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import type { EventForCelebration } from '@/types/celebration.types';

type Mode = 'create' | 'edit';

interface EditorProps {
  mode: Mode;
}

function ServiceRequestEditor({ mode }: EditorProps) {
  const { celebrationId, serviceId, requestId } = useParams<{
    celebrationId: string;
    serviceId: string;
    requestId?: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['services', 'common']);
  const cid = Number(celebrationId);
  const sid = Number(serviceId);
  const rid = requestId ? Number(requestId) : null;

  // Service definition metadata (for name + custom field definitions)
  const [serviceName, setServiceName] = useState<string>('');
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [events, setEvents] = useState<EventForCelebration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [requestName, setRequestName] = useState('');
  const [budget, setBudget] = useState('');
  const [priority, setPriority] = useState<'must_have' | 'optional'>('must_have');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [appliesToEventIds, setAppliesToEventIds] = useState<number[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [summary, celebration] = await Promise.all([
        serviceSuggestionApiService.getShortlistSummary(cid),
        celebrationService.getCelebrationWithEvents(cid).catch(() => null),
      ]);

      const allItems = (summary.by_category ?? []).flatMap((cat) => cat.items);
      const serviceItem = allItems.find((item) => item.service_definition_id === sid);

      setServiceName(serviceItem?.name ?? `Service #${sid}`);
      setServiceDescription(serviceItem?.description ?? '');
      setCustomFieldDefs(serviceItem?.custom_fields ?? []);
      setEvents(celebration?.events ?? []);

      if (mode === 'edit' && rid !== null) {
        const existing = await serviceRequestApiService.getRequest(cid, rid);
        setRequestName(existing.request_name);
        setBudget(existing.budget != null ? String(existing.budget) : '');
        setPriority(existing.priority);
        setAssignedTo(existing.assigned_to ?? '');
        setNotes(existing.notes ?? '');
        setAppliesToEventIds(existing.applies_to_event_ids ?? []);
        setCustomFieldValues(existing.custom_field_values ?? {});
      } else {
        // Create mode: pre-fill from shortlist item values
        setRequestName(serviceItem?.request_name || serviceItem?.name || '');
        if (serviceItem?.budget != null) setBudget(String(serviceItem.budget));
        if (serviceItem?.priority) setPriority(serviceItem.priority);
        if (serviceItem?.assigned_to) setAssignedTo(serviceItem.assigned_to);
        if (serviceItem?.notes) setNotes(serviceItem.notes);
        if (serviceItem?.applies_to_event_ids?.length) setAppliesToEventIds(serviceItem.applies_to_event_ids);
        if (serviceItem?.custom_field_values) setCustomFieldValues(serviceItem.custom_field_values as Record<string, string>);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('services:requestEditor.errors.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [cid, sid, rid, mode]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleEvent = (eventId: number) => {
    setAppliesToEventIds((prev) => {
      if (prev.includes(eventId)) return prev.filter((id) => id !== eventId);
      return [...prev, eventId];
    });
  };

  const handleSave = async (targetStatus: 'draft' | 'open') => {
    const cleanedName = requestName.trim() || serviceName;
    if (!cleanedName) {
      setError(t('services:requestEditor.validation.nameRequired'));
      return;
    }
    if (appliesToEventIds.length === 0) {
      setError(t('services:requestEditor.validation.eventRequired'));
      return;
    }
    try {
      setSaving(true);
      setError(null);

      if (mode === 'create') {
        const created = await serviceRequestApiService.createRequest(cid, {
          service_id: sid,
          request_name: cleanedName,
          budget: budget !== '' ? Number(budget) : undefined,
          priority,
          applies_to_event_ids: appliesToEventIds,
          custom_field_values: customFieldValues,
          status: targetStatus,
          assigned_to: assignedTo.trim() || undefined,
          notes: notes.trim() || undefined,
        });
        navigate(`/celebrations/${cid}/services/${sid}/requests/${created.id}`);
      } else if (rid !== null) {
        await serviceRequestApiService.updateRequest(cid, rid, {
          request_name: cleanedName,
          budget: budget !== '' ? Number(budget) : undefined,
          priority,
          applies_to_event_ids: appliesToEventIds,
          custom_field_values: customFieldValues,
          assigned_to: assignedTo.trim() || null,
          notes: notes.trim() || null,
        });
        if (targetStatus !== 'draft') {
          await serviceRequestApiService.transitionStatus(cid, rid, targetStatus);
        }
        navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}`);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('services:requestEditor.errors.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">{t('services:requestEditor.loading')}</span>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-5">
      <AppBreadcrumbs />

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          {mode === 'create' ? t('services:requestEditor.titleCreate') : t('services:requestEditor.titleEdit')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{t('services:requestEditor.serviceLabel', { name: serviceName })}</p>
        {serviceDescription && (
          <p className="mt-2 text-sm text-gray-600">{serviceDescription}</p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="rounded-xl border border-primary/20 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center gap-2 border-b border-gray-100 pb-4">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
            1
          </span>
          <h2 className="text-base font-semibold text-gray-900">{t('services:requestEditor.section1')}</h2>
        </div>

        <div className="mb-4">
          <label htmlFor="requestName" className="mb-1 block text-sm font-medium text-gray-700">
            {t('services:requestEditor.labels.requestName')} <span className="text-red-500">*</span>
          </label>
          <input
            id="requestName"
            type="text"
            placeholder={t('services:requestEditor.placeholders.requestName')}
            value={requestName}
            onChange={(e) => setRequestName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="budget" className="mb-1 block text-sm font-medium text-gray-700">{t('services:requestEditor.labels.budget')}</label>
            <input
              id="budget"
              type="number"
              min={0}
              step={1}
              placeholder={t('services:requestEditor.placeholders.budget')}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="priority" className="mb-1 block text-sm font-medium text-gray-700">{t('services:requestEditor.labels.priority')}</label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'must_have' | 'optional')}
              className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="must_have">{t('services:requestEditor.priority.mustHave')}</option>
              <option value="optional">{t('services:requestEditor.priority.optional')}</option>
            </select>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="assignedTo" className="mb-1 block text-sm font-medium text-gray-700">{t('services:requestEditor.labels.assignedTo')}</label>
            <input
              id="assignedTo"
              type="text"
              placeholder={t('services:requestEditor.placeholders.assignedTo')}
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="notes" className="mb-1 block text-sm font-medium text-gray-700">{t('services:requestEditor.labels.notes')}</label>
            <textarea
              id="notes"
              rows={3}
              placeholder={t('services:requestEditor.placeholders.notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {customFieldDefs.length > 0 && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {customFieldDefs.map((field: CustomFieldDefinition) => (
              <div key={field.key}>
                <label htmlFor={`cf-${field.key}`} className="mb-1 block text-sm font-medium text-gray-700">
                  {field.label}
                </label>
                {field.field_type === 'dropdown' && field.options && field.options.length > 0 ? (
                  <select
                    id={`cf-${field.key}`}
                    value={customFieldValues[field.key] ?? ''}
                    onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`cf-${field.key}`}
                    type={field.field_type === 'number' ? 'number' : 'text'}
                    min={field.field_type === 'number' ? 0 : undefined}
                    placeholder={field.label}
                    value={customFieldValues[field.key] ?? ''}
                    onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4">
          <p className="mb-2 block text-sm font-medium text-gray-700">
            {t('services:requestEditor.labels.appliesToEvents')} <span className="text-red-500">*</span>
          </p>
          {events.length === 0 ? (
            <div className="rounded-md border border-dashed border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
              <p className="font-medium">{t('services:requestEditor.events.none')}</p>
              <p className="mt-0.5 text-xs">{t('services:requestEditor.events.noneHint')}</p>
            </div>
          ) : (
            <div className="space-y-2 rounded-md border border-gray-200 p-3">
              {events.map((event) => {
                const checked = appliesToEventIds.includes(event.id);
                return (
                  <label key={event.id} className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleEvent(event.id)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="font-medium text-gray-900">{event.title}</span>
                      <span className="ml-1 text-gray-500">
                        ({event.event_type}
                        {event.event_date ? ` - ${new Date(event.event_date).toLocaleDateString()}` : ''})
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {appliesToEventIds.length === 0 && events.length > 0 ? (
              <span className="text-yellow-700 font-medium">{t('services:requestEditor.events.selectWarning')}</span>
            ) : (
              t('services:requestEditor.events.hint')
            )}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() =>
              rid !== null
                ? navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}`)
                : navigate(`/celebrations/${cid}/services/${sid}`)
            }
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => handleSave('draft')}
            disabled={saving || appliesToEventIds.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('services:requestEditor.buttons.saveDraft')}
          </button>
          <button
            type="button"
            onClick={() => handleSave('open')}
            disabled={saving || appliesToEventIds.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('services:requestEditor.buttons.openForVendors')}
          </button>
        </div>
      </div>
    </PageContainer>
  );
}

export function ServiceRequestNewPage() {
  return <ServiceRequestEditor mode="create" />;
}

export function ServiceRequestUpdatePage() {
  return <ServiceRequestEditor mode="edit" />;
}
